<script setup lang="ts">
import zhCN from 'ant-design-vue/es/locale/zh_CN';
import 'dayjs/locale/zh-cn';
import dayjs from "dayjs";
import pb, {getCurrentUser} from "./comm/pb";
import {computed, ref, watchEffect} from "vue";
import {useRouter} from "vue-router";
import {message} from "ant-design-vue";
import {MenuFoldOutlined, MenuUnfoldOutlined} from '@ant-design/icons-vue';
import {onMounted, onUnmounted} from 'vue';
import * as Sentry from "@sentry/vue";


dayjs().locale('zh-cn');

const router = useRouter();

const isLoggedIn = ref(pb.authStore.isValid);
const currentUser = computed(() => getCurrentUser());

pb.authStore.onChange(() => {
  isLoggedIn.value = pb.authStore.isValid;
});

pb.afterSend = (response, data) => {
  if (!response.ok) {
    message.error(data?.message || '请求失败');
    Sentry.captureException(new Error(data?.message || '请求失败'));
  }
  return data;
};

if (isLoggedIn.value) {
  router.push('/menus');
}


watchEffect(() => {
  if (isLoggedIn.value) {
    Sentry.setUser({
      email: currentUser.value?.email,
      id: currentUser.value?.id,
    });
  }
});

const handleLogout = () => {
  Sentry.setUser(null);
  pb.authStore.clear();
  router.push('/login');
};

const isMobile = ref(false);
const isDrawerVisible = ref(false);

const checkIsMobile = () => {
  isMobile.value = window.innerWidth <= 768;
};

onMounted(() => {
  checkIsMobile();
  window.addEventListener('resize', checkIsMobile);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkIsMobile);
});

const toggleDrawer = () => {
  isDrawerVisible.value = !isDrawerVisible.value;
};

const isDev = import.meta.env.MODE === 'development';
</script>

<template>
  <a-config-provider :locale="zhCN">
    <template v-if="isLoggedIn">
      <a-layout>
        <!-- 固定的顶部导航栏 -->
        <a-layout-header class="fixed-header">
          <!-- 移动端菜单按钮 -->
          <div v-if="isMobile">
            <a-button type="text" @click="toggleDrawer">
              <MenuUnfoldOutlined v-if="!isDrawerVisible"/>
              <MenuFoldOutlined v-else/>
            </a-button>
          </div>
          <div style="flex: 1"></div>
          <a-space>
            <a-typography-text>
              {{ currentUser?.email }}
            </a-typography-text>
            <a-divider type="vertical"/>
            <a-button type="link" @click="handleLogout">
              登出
            </a-button>
          </a-space>
        </a-layout-header>

        <!-- 主要内容区域，添加顶部边距 -->
        <a-layout class="main-content">
          <!-- PC端侧边栏 -->
          <a-layout-sider v-if="!isMobile" width="200" style="background: #fff">
            <a-menu
                mode="inline"
                :style="{ height: '100%', borderRight: 0 }"
                :selected-keys="[$route.path]"
            >
              <a-menu-item key="/groups">
                <router-link to="/groups">分类管理</router-link>
              </a-menu-item>
              <a-menu-item key="/ingredients">
                <router-link to="/ingredients">原料管理</router-link>
              </a-menu-item>
              <a-menu-item key="/dishes">
                <router-link to="/dishes">菜品管理</router-link>
              </a-menu-item>
              <a-menu-item key="/menus">
                <router-link to="/menus">菜单管理</router-link>
              </a-menu-item>
              <template v-if="isDev">
                <a-menu-item key="/debug">
                  <router-link to="/debug">调试</router-link>
                </a-menu-item>
              </template>
            </a-menu>
          </a-layout-sider>

          <a-layout>
            <a-layout-content style="padding: 0 24px 24px">
              <a-layout-content
                  :style="{ background: '#fff', padding: '24px', margin: 0, minHeight: '280px' }"
              >
                <router-view></router-view>
              </a-layout-content>
            </a-layout-content>
          </a-layout>

          <!-- 移动端抽屉菜单 -->
          <a-drawer
              v-if="isMobile"
              placement="left"
              :visible="isDrawerVisible"
              :closable="false"
              :width="250"
              :mask="true"
              :mask-closable="true"
              @close="isDrawerVisible = false"
              :body-style="{ padding: 0 }"
          >
            <a-menu
                mode="inline"
                :style="{ height: '100%', borderRight: 0 }"
                :selected-keys="[$route.path]"
                @click="isDrawerVisible = false"
            >
              <a-menu-item key="/groups">
                <router-link to="/groups">分类管理</router-link>
              </a-menu-item>
              <a-menu-item key="/ingredients">
                <router-link to="/ingredients">原料管理</router-link>
              </a-menu-item>
              <a-menu-item key="/dishes">
                <router-link to="/dishes">菜品管理</router-link>
              </a-menu-item>
              <a-menu-item key="/menus">
                <router-link to="/menus">菜单管理</router-link>
              </a-menu-item>
              <template v-if="isDev">
                <a-menu-item key="/debug">
                  <router-link to="/debug">调试</router-link>
                </a-menu-item>
              </template>
            </a-menu>
          </a-drawer>
        </a-layout>
      </a-layout>
    </template>
    <template v-else>
      <router-view></router-view>
    </template>
  </a-config-provider>
</template>

<style scoped>
.site-layout-background {
  background: #fff;
}

.ant-layout-header {
  height: 64px;
  line-height: 64px;
  border-bottom: 1px solid #f0f0f0;
}

/* 移动端样式调整 */
@media (max-width: 768px) {
  :deep(.ant-layout-header) {
    padding: 0 12px !important;
  }

  :deep(.ant-layout-content) {
    padding: 0 12px 12px !important;
  }
}

/* 抽屉样式调整 */
:deep(.ant-drawer-content-wrapper) {
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
}

:deep(.ant-drawer .ant-menu) {
  border-right: none;
}

.fixed-header {
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  z-index: 100;
  background: #fff;
  padding: 0 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #f0f0f0;
}

.main-content {
  margin-top: 64px;
  min-height: calc(100vh - 64px);
}

@media (max-width: 768px) {
  .fixed-header {
    padding: 0 12px;
  }

  :deep(.ant-layout-content) {
    padding: 0 12px 12px !important;
  }
}
</style>
